import { ConditionNode, FilterOperator, LogicalNode, LogicalOperator, Node, OrderBy, Pagination, RecordsResponse, ReferenceMode, SortDirection, UNARY_OPERATORS } from "app/types/record-management-types";
import { axiosInterceptor } from "app/services/http-services/request-interceptor";
import { AxiosInstance } from "axios";
import SysRecord from "./record";
import { RecordPermissions } from "modules/record/shared/services/actionPermissions";

type RecordManagementErrorSummary = { table: string; scope: string; message: string; code?: string; at: string };
let lastRecordManagementError: RecordManagementErrorSummary | null = null;
export const getLastRecordManagementError = () => lastRecordManagementError;

export class RecordManagement<T = SysRecord> {
    private static readonly API_ENDPOINT = "/api/records";

    private readonly table: string;
    private readonly client: AxiosInstance = axiosInterceptor;

    private root: LogicalNode;
    private stack: LogicalNode[];

    private orderByList: OrderBy[] = [];
    private pagination?: Pagination;
    private limitValue?: number;
    private referenceModeValue?: ReferenceMode;
    private translationLanguageValue?: string;

    constructor(table: string) {
        this.table = table;

        this.root = this.createLogicalNode("and");
        this.stack = [this.root];
    }

    self = this;

    private logExecutionError(scope: string, error: any) {
        lastRecordManagementError = {
            table: this.table,
            scope,
            message: String(error?.response?.data?.error?.message || error?.message || "Unknown record management error"),
            code: error?.response?.data?.error?.code,
            at: new Date().toISOString(),
        };
        console.error(
            scope,
            error?.response?.data || error?.message || error
        );
    }

    private attachResponseMeta<TValue>(data: TValue, meta: any): TValue {
        if (!data || !meta || typeof data !== "object") return data;

        Object.defineProperty(data as object, "meta", {
            configurable: true,
            enumerable: false,
            value: meta,
        });
        return data;
    }

    /* =======================
     * Query Builder
     * ======================= */

    where(field: string, value: any): this;
    where(field: string, operator: FilterOperator, value: any): this;
    where(field: string, operatorOrValue: any, value?: any): this {
        const { operator, value: finalValue } =
            this.normalizeWhereArgs(operatorOrValue, value);

        const condition = this.createConditionNode(field, operator, finalValue);
        this.currentGroup().conditions.push(condition);
        return this;
    }

    orWhere(field: string, value: any): this;
    orWhere(field: string, operator: FilterOperator, value: any): this;
    orWhere(field: string, operatorOrValue: any, value?: any): this {
        const { operator, value: finalValue } =
            this.normalizeWhereArgs(operatorOrValue, value);

        const condition = this.createConditionNode(field, operator, finalValue);
        const group = this.currentGroup();

        if (group.operator === "or") {
            group.conditions.push(condition);
            return this;
        }

        const last = group.conditions.pop();

        if (!last) {
            group.conditions.push(this.createLogicalNode("or", [condition]));
            return this;
        }

        const orGroup = this.createLogicalNode("or", [last, condition]);
        group.conditions.push(orGroup);

        return this;
    }

    beginGroup(operator: LogicalOperator): this {
        const group = this.createLogicalNode(operator);
        this.currentGroup().conditions.push(group);
        this.stack.push(group);
        return this;
    }

    endGroup(): this {
        if (this.stack.length === 1) {
            throw new Error("Cannot end root group");
        }

        const group = this.stack.pop()!;
        if (group.conditions.length === 0) {
            throw new Error("Logical group cannot be empty");
        }

        return this;
    }

    builderFromQuery(query: LogicalNode): this {
        // Reset state
        this.clearQuery();

        // Deep clone to avoid external mutation
        const cloned = this.cloneNode(query) as LogicalNode;

        // Replace root
        this.root = cloned;

        // Rebuild stack (root only)
        this.stack = [this.root];

        return this;
    }

    /* =======================
     * Ordering
     * ======================= */

    orderBy(field: string, direction: SortDirection = "asc"): this {
        this.orderByList.push({ field, direction });
        return this;
    }

    /* =======================
     * Pagination
     * ======================= */

    paginate(page: number, perPage: number): this {
        this.pagination = { page, perPage };
        return this;
    }

    /* =======================
     * Limit
     * ======================= */
    limit(value: number): this {
        if (value <= 0) {
            throw new Error("Limit must be greater than zero");
        }

        this.limitValue = value;
        return this;
    }

    referenceMode(mode: ReferenceMode): this {
        this.referenceModeValue = mode;
        return this;
    }

    translationLanguage(language?: string | null): this {
        this.translationLanguageValue = language || undefined;
        return this;
    }

    /* =======================
     * Execution
     * ======================= */

    async run(): Promise<RecordsResponse<T>> {
        const params = this.buildParams();

        const response = await this.client.get<RecordsResponse<T>>(
            `${RecordManagement.API_ENDPOINT}/${this.table}`,
            { params }
        ).catch((error) => {
            this.logExecutionError("Error executing record management query:", error);
            throw error;
        });

        return response.data;
    }

    async get(id: string): Promise<T> {
        const params = this.buildParams();

        const response = await this.client.get<T>(
            `${RecordManagement.API_ENDPOINT}/${this.table}/${id}`,
            { params }
        ).catch((error) => {
            this.logExecutionError("Error executing record management query:", error);
            throw error;
        });

        return response.data;
    }


    async fieldPermissions(writeAction: "create" | "update" = "create"): Promise<{ fieldPermissions: Record<string, "hidden" | "readonly">; permissions?: RecordPermissions }> {
        const response = await this.client.get<{ success: boolean; data: any; meta: { field_permissions?: Record<string, "hidden" | "readonly">; permissions?: RecordPermissions }; error: any }>(
            `${RecordManagement.API_ENDPOINT}/${this.table}/field_permissions`,
            { params: { write_action: writeAction } }
        ).catch((error) => {
            this.logExecutionError("Error loading record field permissions:", error);
            throw error;
        });

        return { fieldPermissions: response.data.meta?.field_permissions ?? {}, permissions: response.data.meta?.permissions };
    }

    async first(): Promise<T | null> {
        const original = this.limitValue;
        this.limitValue = 1;

        const response = await this.run();

        this.limitValue = original;

        var data = response.data[0] ?? {};
        data = typeof data === "string" ? JSON.parse(data) : data;

        return data as T || null;
    }

    async save(data: Partial<T>): Promise<T> {
        const response = await this.client
            .post<{ success: boolean; data: T; meta: any; error: any }>(
                `${RecordManagement.API_ENDPOINT}/${this.table}`,
                data
            )
            .catch((error) => {
                this.logExecutionError("Error executing record management save:", error);
                throw error;
            });
        return this.attachResponseMeta(response.data.data, response.data.meta);
    }

    async update(id: string, data: Partial<T>): Promise<T> {
        const response = await this.client.patch<{ success: boolean; data: T; meta: any; error: any }>(
            `${RecordManagement.API_ENDPOINT}/${this.table}/${id}`,
            data
        ).catch((error) => {
            this.logExecutionError("Error executing record management update:", error);
            throw error;
        });

        return this.attachResponseMeta(response.data.data, response.data.meta);
    }

    async delete(id: string): Promise<void> {
        await this.client.delete(
            `${RecordManagement.API_ENDPOINT}/${this.table}/${id}`
        ).catch((error) => {
            this.logExecutionError("Error executing record management delete:", error);
            throw error;
        });
    }

    /* =======================
     * Utilities
     * ======================= */

    clearQuery(): this {
        this.root = this.createLogicalNode("and");
        this.stack = [this.root];
        return this;
    }

    getQuery(): LogicalNode {
        if (this.root.conditions.length === 0) {
            throw new Error("Query cannot be empty");
        }

        return this.cloneNode(this.root) as LogicalNode;
    }

    private buildParams(): Record<string, unknown> {
        const params: Record<string, unknown> = {};

        if (this.root.conditions.length > 0) {
            params.query = this.getQuery();
        }

        if (this.orderByList.length > 0) {
            params.order_by = this.orderByList.length === 1
                ? this.orderByList[0]
                : JSON.stringify(this.orderByList);
        }

        if (this.pagination) {
            params.page = this.pagination.page;
            params.per_page = this.pagination.perPage;
        }

        if (this.limitValue !== undefined) {
            params.limit = this.limitValue;
        }

        if (this.referenceModeValue) {
            params.reference_mode = this.referenceModeValue;
        }

        if (this.translationLanguageValue) {
            params.translation_language = this.translationLanguageValue;
        }

        return params;
    }


    private currentGroup(): LogicalNode {
        return this.stack[this.stack.length - 1];
    }

    private createLogicalNode(
        operator: LogicalOperator,
        conditions: Node[] = []
    ): LogicalNode {
        return { type: "logical", operator, conditions };
    }

    private createConditionNode(
        field: string,
        operator: string,
        value?: unknown
    ): ConditionNode {
        const node: ConditionNode = {
            type: "condition",
            operator,
            field,
        };
        if (value !== undefined) {
            node.value = value;
        }
        return node;
    }

    private cloneNode(node: Node): Node {
        if (node.type === "condition") {
            return { ...node };
        }

        if (node.type === "exists") {
            return {
                type: "exists",
                table: node.table,
                conditions: node.conditions.map(condition => ({ ...condition }))
            };
        }

        return {
            type: "logical",
            operator: node.operator,
            conditions: node.conditions.map(n => this.cloneNode(n))
        };
    }

    private normalizeWhereArgs = (
        operatorOrValue: any,
        value?: any
    ): { operator: FilterOperator; value?: any } => {
        // Caso explícito: where(field, operator)
        if (value === undefined && UNARY_OPERATORS.has(operatorOrValue)) {
            return {
                operator: operatorOrValue,
                value: undefined,
            };
        }

        // Caso padrão: where(field, value)
        if (value === undefined) {
            return {
                operator: "eq",
                value: operatorOrValue,
            };
        }

        // Caso explícito: where(field, operator, value)
        return {
            operator: operatorOrValue,
            value,
        };
    }
}
